import { useState, useEffect, useCallback, useMemo } from "react";

import { TextField, FilledTextFieldProps, InputAdornment, Typography } from "@mui/material";
import { formatDuration, intervalToDuration } from "date-fns";
import { hu } from "date-fns/locale";
import { useLocale } from "../../../locales/LocaleProvider";
import { onNumberInputClick } from "../../../utils/utils";

const MAX = 24 * 60 * 60 * 60 * 100 - 1;

export const parseValue = (value: string): number => {
  let parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    parsed = 0;
  }
  parsed = Math.min(parsed, MAX);
  return parsed;
};

/**
 * zeroLabel {string} (opcionális) A 0 érték címkéjét cseréli le
 * unit {number} (opcionális, default: 100) A beírt 1 érték hány millisecundumnak felel meg
 */
interface TimeTextFieldProps extends FilledTextFieldProps {
  zeroLabel?: string;
  unit?: number;
}

const TimeTextField = ({ value, label, onChange, onBlur, zeroLabel, unit = 10, ...rest }: TimeTextFieldProps) => {
  const unitCorrector = useMemo(() => 1000 / unit, [unit]);
  const [readable, setReadable] = useState("");
  const [correctedValue, setCorrectedValue] = useState("");

  const { locale } = useLocale();

  const onCorrectedChange = useCallback(
    (e) => {
      setCorrectedValue(e.target.value);
      if (onChange) {
        if (e.target.value) {
          e.target.value = `${parseInt(e.target.value, 10) * unitCorrector}`;
        }
        onChange(e);
      }
    },
    [onChange, unitCorrector]
  );

  const formatToReadable = useCallback(
    (value: string): string => {
      const parsed = parseValue(value);
      if (parsed === 0 && zeroLabel) {
        return zeroLabel;
      }
      const duration = intervalToDuration({ start: 0, end: parsed * unit });
      duration.seconds = (duration.seconds || 0) + (parsed % unitCorrector) / unitCorrector;
      const dateLocale = locale === "hu" ? hu : undefined;
      return (
        formatDuration(duration, { format: ["hours", "minutes", "seconds"], locale: dateLocale }) ||
        formatDuration(duration, { format: ["seconds"], zero: true, locale: dateLocale })
      );
    },
    [locale, zeroLabel, unit, unitCorrector]
  );

  useEffect(() => {
    setReadable(formatToReadable(`${value}`));
    setCorrectedValue(value ? `${parseInt(value as string, 10) / unitCorrector}` : "");
  }, [value, unitCorrector, formatToReadable]);

  return (
    <div style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap" }}>
      <TextField
        {...rest}
        label={label}
        value={correctedValue}
        onChange={onCorrectedChange}
        onBlur={onBlur}
        inputProps={{ min: 0, max: MAX, type: "number", onClick: onNumberInputClick }}
        autoComplete="off"
        InputProps={{
          endAdornment: correctedValue ? (
            <InputAdornment position="end" disableTypography>
              <Typography variant="caption" sx={{ alignSelf: "flex-start" }}>
                {readable}
              </Typography>
            </InputAdornment>
          ) : null,
        }}
      />
    </div>
  );
};

export default TimeTextField;
